import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/Card';
import { LineChart } from '../../components/charts';

export default function SearchVolume() {
  const initialBrands = [];
  const [brandsList, setBrandsList] = useState(initialBrands);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [savedKeywords, setSavedKeywords] = useState([]);
  const [loadingSaved, setLoadingSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingIds, setDeletingIds] = useState({});

  // Dynamic monthly volumes fetched from backend: { brandName: [{year, month, monthly_searches}...] }
  const [brandToMonthly, setBrandToMonthly] = useState({});
  const [kpKeyword, setKpKeyword] = useState('');
  const [kpLoading, setKpLoading] = useState(false);
  const [kpMonthly, setKpMonthly] = useState(null);
  const currentYear = new Date().getFullYear();
  const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];

  const getMonthlyValue = (brand, year, monthIndex) => {
    const arr = brandToMonthly[brand];
    if (!Array.isArray(arr)) return 0;
    const monthNum = monthIndex + 1;
    const row = arr.find(r => Number(r.year) === year && Number(r.month) === monthNum);
    return row ? Number(row.monthly_searches || 0) : 0;
  };

  const fetchBrandMonthly = async (brand) => {
    const brandId = localStorage.getItem('currentBrandId');
    const requestMonthly = async (url) => {
      try {
        const res = await fetch(url, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json', ...(brandId ? { 'X-Brand-ID': brandId } : {}) },
          body: JSON.stringify({ keyword: brand, months: 0 })
        });
        const ct = res.headers.get('content-type') || '';
        if (!res.ok) {
          let msg = 'Failed to fetch keyword volume';
          if (ct.includes('application/json')) {
            const js = await res.json().catch(() => ({}));
            msg = js.error || msg;
          }
          return { ok: false, error: msg };
        }
        const data = await res.json();
        return { ok: true, data };
      } catch (e) {
        return { ok: false, error: e?.message || 'Request failed' };
      }
    };
    try {
      // Prefer the search-volume router; fallback to legacy google-ads route if needed.
      const primary = await requestMonthly('https://api.glowytics.com/api/search-volume/monthly');
      const secondary = primary.ok ? primary : await requestMonthly('https://api.glowytics.com/api/google-ads/search-volume/monthly');
      if (!secondary.ok) {
        throw new Error(secondary.error || 'Failed to fetch keyword volume');
      }
      const data = secondary.data || {};
      const monthly = Array.isArray(data.monthly) ? data.monthly : [];
      setBrandToMonthly(prev => ({ ...prev, [brand]: monthly }));
    } catch (e) {
      console.error('Fetch monthly error for', brand, e);
      setBrandToMonthly(prev => ({ ...prev, [brand]: [] }));
    }
  };

  useEffect(() => {
    // open current year by default
    setExpanded(prev => ({ ...prev, [String(currentYear)]: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load saved keywords on mount and fetch volumes for each
  useEffect(() => {
    const loadSaved = async () => {
      try {
        setLoadingSaved(true);
        const brandId = localStorage.getItem('currentBrandId');
        const res = await fetch('https://api.glowytics.com/api/search-volume/keywords', {
          method: 'GET',
          credentials: 'include',
          headers: { ...(brandId ? { 'X-Brand-ID': brandId } : {}) }
        });
        if (!res.ok) {
          setSavedKeywords([]);
          return;
        }
        const data = await res.json();
        const keywords = Array.isArray(data.keywords) ? data.keywords : [];
        setSavedKeywords(keywords);
        // derive brand names and fetch volumes
        const names = keywords.map(k => k.keyword).filter(Boolean);
        if (names.length) {
          setBrandsList(names);
          setSelectedBrands(names);
          // fetch sequentially to avoid rate limits
          for (const kw of names) {
            // eslint-disable-next-line no-await-in-loop
            await fetchBrandMonthly(kw);
          }
        }
      } catch (e) {
        setSavedKeywords([]);
      } finally {
        setLoadingSaved(false);
      }
    };
    loadSaved();
  }, []);  

  // Build months for 2022..currentYear using fetched volumes
  const years = [];
  for (let y = currentYear; y >= 2022; y--) years.push(y);
  const dataByYear = {};
  years.forEach((y) => {
    dataByYear[y] = monthNames.map((name, idx) => ({ name, idx, volumes: Object.fromEntries(brandsList.map(b => [b, getMonthlyValue(b, y, idx)])) }));
  });

  // Colors for multiple brands in the chart
  const palette = ['#2563eb', '#16a34a', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#f97316', '#22c55e'];
  const paletteFill = ['rgba(37,99,235,0.12)','rgba(22,163,74,0.12)','rgba(245,158,11,0.12)','rgba(239,68,68,0.12)','rgba(139,92,246,0.12)','rgba(6,182,212,0.12)','rgba(249,115,22,0.12)','rgba(34,197,94,0.12)'];

  // Build last 12 months dataset for selected brands
  const last12Data = (() => {
    const rows = [];
    for (let i = 11; i >= 0; i--) {
      const d = new Date();
      d.setMonth(d.getMonth() - i);
      const y = d.getFullYear();
      const mIdx = d.getMonth();
      const label = `${monthNames[mIdx].slice(0, 3)} ${y}`;
      const row = { label };
      selectedBrands.forEach((b) => {
        row[b] = getMonthlyValue(b, y, mIdx);
      });
      rows.push(row);
    }
    return rows;
  })();
  const chartJsData = {
    labels: last12Data.map(r => r.label),
    datasets: selectedBrands.map((b, i) => ({
      label: b,
      data: last12Data.map(r => Number(r[b] || 0)),
      borderColor: palette[i % palette.length],
      backgroundColor: paletteFill[i % paletteFill.length],
      fill: true,
      pointRadius: 0,
      borderWidth: 2,
      tension: 0.35
    }))
  };
  const chartJsOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { display: true },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const value = Number(ctx.parsed.y || 0).toLocaleString('tr-TR');
            return `${ctx.dataset.label}: ${value}`;
          }
        }
      }
    },
    scales: {
      x: { ticks: { color: '#64748b', font: { size: 10 } }, grid: { color: '#f1f5f9' } },
      y: { ticks: { color: '#64748b', font: { size: 10 } }, grid: { color: '#f1f5f9' } }
    }
  };

  const toggleBrand = (b) => {
    setSelectedBrands((prev) => prev.includes(b) ? prev.filter(x => x !== b) : [...prev, b]);
  };

  const format = (n) => Number(n).toLocaleString('tr-TR');
  const upToCurrentMonth = new Date().getMonth();
  const yearTotal = (year, brand) => {
    const endIdx = year === currentYear ? upToCurrentMonth : 11;
    let sum = 0;
    for (let i = 0; i <= endIdx; i++) sum += dataByYear[year][i].volumes[brand] || 0;
    return sum;
  };

  const fetchKpMonthly = async () => {
    try {
      setKpLoading(true);
      setKpMonthly(null);
      if (!kpKeyword || !kpKeyword.trim()) return;
      const brandId = localStorage.getItem('currentBrandId');
      const res = await fetch('https://api.glowytics.com/api/labs/keyword-planner/monthly-volume', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...(brandId ? { 'X-Brand-ID': brandId } : {}) },
        body: JSON.stringify({ keyword: kpKeyword.trim(), months: 0 })
      });
      const ct = res.headers.get('content-type') || '';
      if (!res.ok) {
        let msg = 'Request failed';
        if (ct.includes('application/json')) {
          const js = await res.json().catch(() => ({}));
          msg = js.error || msg;
        }
        throw new Error(msg);
      }
      const data = await res.json();
      setKpMonthly(Array.isArray(data.monthly) ? data.monthly : []);
    } catch (e) {
      console.error('Keyword Planner error:', e);
      setKpMonthly([]);
    } finally {
      setKpLoading(false);
    }
  };

  // Add brand modal helpers
  // No predefined brands; user can add any term manually
  const brandDirectory = [];
  const filteredDirectory = brandDirectory
    .filter(b => b.toLowerCase().includes(searchText.toLowerCase()))
    .filter(b => !brandsList.includes(b));
  const handleAddBrand = async (name) => {
    if (!name) return;
    const keyword = name.trim();
    if (!keyword) return;
    try {
      setSaving(true);
      const brandId = localStorage.getItem('currentBrandId');
      const res = await fetch('https://api.glowytics.com/api/search-volume/keywords/save', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...(brandId ? { 'X-Brand-ID': brandId } : {}) },
        body: JSON.stringify({ keyword })
      });
      const ok = res.ok;
      const data = ok ? await res.json() : null;
      if (ok && data?.success) {
        // update saved list
        setSavedKeywords(prev => {
          const exists = prev.find(k => k.keyword.toLowerCase() === keyword.toLowerCase());
          const item = { id: data.id, keyword, languageConstant: 'languageConstants/1000', geoTargetConstant: 'geoTargetConstants/2792', platform: 'GOOGLE_SEARCH', isActive: true };
          if (exists) {
            return prev.map(k => (k.id === exists.id ? { ...exists, ...item } : k));
          }
          return [item, ...prev];
        });
      }
    } catch {
      // ignore
    } finally {
      setSaving(false);
    }
    if (!brandsList.includes(keyword)) {
      setBrandsList(prev => [...prev, keyword]);
      fetchBrandMonthly(keyword);
    }
    if (!selectedBrands.includes(keyword)) setSelectedBrands(prev => [...prev, keyword]);
    setIsAddOpen(false);
    setSearchText('');
  };

  const deleteKeyword = async (keyword) => {
    const entry = savedKeywords.find(k => (k.keyword || '').toLowerCase() === keyword.toLowerCase());
    if (!entry?.id) {
      // remove locally
      setBrandsList(prev => prev.filter(b => b !== keyword));
      setSelectedBrands(prev => prev.filter(b => b !== keyword));
      setSavedKeywords(prev => prev.filter(k => (k.keyword || '').toLowerCase() !== keyword.toLowerCase()));
      return;
    }
    try {
      setDeletingIds(prev => ({ ...prev, [entry.id]: true }));
      const brandId = localStorage.getItem('currentBrandId');
      const tryDelete = async (url) => {
        const res = await fetch(url, {
          method: 'DELETE',
          credentials: 'include',
          headers: { ...(brandId ? { 'X-Brand-ID': brandId } : {}) }
        });
        if (res.ok) return true;
        return false;
      };
      const trySoftDelete = async (url) => {
        const res = await fetch(url, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json', ...(brandId ? { 'X-Brand-ID': brandId } : {}) },
          body: JSON.stringify({
            keyword: entry.keyword,
            languageConstant: entry.languageConstant,
            geoTargetConstant: entry.geoTargetConstant,
            platform: entry.platform || 'GOOGLE_SEARCH',
            notes: entry.notes || null,
            isActive: false
          })
        });
        if (!res.ok) return false;
        const data = await res.json().catch(() => ({}));
        return !!data?.success;
      };
      // Prefer the dedicated search-volume router, but fall back to legacy google-ads route.
      const ok =
        (await tryDelete(`https://api.glowytics.com/api/search-volume/keywords/${entry.id}`)) ||
        (await tryDelete(`https://api.glowytics.com/api/google-ads/search-volume/keywords/${entry.id}`)) ||
        // If delete endpoints are missing, soft-delete via save (isActive:false).
        (await trySoftDelete('https://api.glowytics.com/api/search-volume/keywords/save')) ||
        (await trySoftDelete('https://api.glowytics.com/api/google-ads/search-volume/keywords/save'));
      if (ok) {
        setBrandsList(prev => prev.filter(b => b !== keyword));
        setSelectedBrands(prev => prev.filter(b => b !== keyword));
        setSavedKeywords(prev => prev.filter(k => k.id !== entry.id));
      } else {
        console.error('Delete keyword failed for', entry.id);
      }
    } catch {
      // ignore
    } finally {
      setDeletingIds(prev => {
        const n = { ...prev };
        delete n[entry.id];
        return n;
      });
    }
  };

  return (
    <>
    <div className="p-6 space-y-6">
      <Card className="rounded-2xl overflow-hidden shadow-xl border border-gray-100">
      <div className="p-6 bg-white text-gray-900 border-b flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="/SearchVolume.svg" 
                alt="Google Search Volume" 
                className="w-14 h-14 object-contain"
              />
              <div>
                <h1 className="text-2xl tracking-tight">Google Search Volume</h1>
                <p className="text-sm text-gray-500">Monthly Google search volume for selected keywords. Last 12 months shown; previous years are expandable.</p>
              </div>
            </div>
          </div>

          {/* Keyword controls */}
          <div className="p-4 border-b bg-white">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                {brandsList.map(b => (
                  <button
                    key={b}
                    type="button"
                    className={`px-3 py-1.5 rounded-full text-xs border transition group relative pr-9 ${selectedBrands.includes(b) ? 'bg-black text-white border-black' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'}`}
                    onClick={() => toggleBrand(b)}
                    title="Toggle on chart"
                  >
                    {b}
                    <span
                      role="button"
                      aria-label={`Remove ${b}`}
                      onClick={(e) => { e.stopPropagation(); deleteKeyword(b); }}
                      className="absolute right-1.5 top-1/2 -translate-y-1/2 inline-flex items-center justify-center w-5 h-5 rounded-full border text-[11px] 
                                 bg-white/80 text-gray-500 border-gray-300 hover:bg-red-600 hover:text-white hover:border-red-600 shadow-sm"
                      title="Remove keyword"
                    >
                      {(() => {
                        const entry = savedKeywords.find(k => (k.keyword || '').toLowerCase() === b.toLowerCase());
                        const isDel = entry && deletingIds[entry.id];
                        return isDel ? '…' : '×';
                      })()}
                    </span>
                  </button>
                ))}
                <button
                  className="px-3 py-1.5 rounded-full text-xs border border-dashed hover:border-gray-400 text-gray-700 bg-white"
                  onClick={() => setIsAddOpen(true)}
                >+ Add brand</button>
              </div>
            </div>
          </div>

          {/* Content area: chart + table when keywords selected, otherwise welcome state */}
          {selectedBrands.length > 0 ? (
            <>
              {/* Combined line chart for last 12 months (selected brands) */}
              <div className="px-6 py-4 bg-white">
                <div className="mb-2 text-sm text-gray-600">Last 12 months</div>
                <div className="h-72">
                  <LineChart data={chartJsData} options={chartJsOptions} />
                </div>
              </div>

              <div className="px-6 py-4 bg-white">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '24%' }}>Period</th>
                        {selectedBrands.map(b => (
                          <th key={b} className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{b}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {years.map((yearLoop) => {
                        const key = `${yearLoop}`;
                        const isOpen = !!expanded[key];
                        const toggle = () => {
                          setExpanded(prev => ({ ...prev, [key]: !isOpen }));
                        };
                        return (
                          <React.Fragment key={key}>
                            <tr className="bg-gray-50">
                              <td className="px-4 py-3 text-sm font-medium text-gray-900 cursor-pointer" onClick={toggle}>
                                <span className="mr-2">{isOpen ? '▾' : '▸'}</span>{yearLoop}
                              </td>
                              {selectedBrands.map(b => (
                                <td key={`${key}-tot-${b}`} className="px-4 py-3 text-sm text-right font-semibold">{format(yearTotal(yearLoop, b))}</td>
                              ))}
                            </tr>
                            {isOpen && (
                              (yearLoop === currentYear
                                ? dataByYear[yearLoop].filter(m => m.idx <= upToCurrentMonth).slice().reverse()
                                : dataByYear[yearLoop].slice().reverse()
                              ).map((m) => (
                                <tr key={`${key}-m-${m.idx}`}>
                                  <td className="px-4 py-3 text-sm text-gray-700 pl-8">{monthNames[m.idx]}</td>
                                  {selectedBrands.map(b => (
                                    <td key={`${key}-${b}-m-${m.idx}`} className="px-4 py-3 text-sm text-right">{format(m.volumes[b] || 0)}</td>
                                  ))}
                                </tr>
                              ))
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          ) : (
            <div className="px-6 py-16 bg-white">
              <div className="flex flex-col items-center justify-center text-center gap-3">
                <img src="/searchtrend.svg" alt="Search trend" className="w-24 h-24" />
                <h3 className="text-lg font-medium text-gray-800">Add keywords to view Google search volumes</h3>
                <p className="text-sm text-gray-500 max-w-md">Use the “+ Add brand” button to add brand keywords you want to track. We’ll fetch monthly volumes from Google Ads and display them here.</p>
                <button
                  className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  onClick={() => setIsAddOpen(true)}
                >
                  + Add brand
                </button>
              </div>
            </div>
          )}
      </Card>
    </div>
    {isAddOpen && (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="absolute inset-0 bg-black/30" onClick={() => setIsAddOpen(false)}></div>
        <div className="relative bg-white rounded-2xl shadow-xl border w-full max-w-lg mx-4">
          <div className="p-5 border-b flex items-center justify-between">
            <h3 className="text-lg font-semibold">Add Keyword</h3>
            <button className="text-gray-500 hover:text-gray-700" onClick={() => setIsAddOpen(false)}>✕</button>
          </div>
          <div className="p-5 space-y-4">
            <input
              autoFocus
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg text-sm"
              placeholder="Type keyword..."
            />
            <div className="max-h-64 overflow-auto divide-y">
              {filteredDirectory.length ? filteredDirectory.map((name) => (
                <button
                  key={name}
                  className="w-full text-left px-3 py-2 hover:bg-gray-50"
                  onClick={() => handleAddBrand(name)}
                >{name}</button>
              )) : (
                <div className="px-3 py-8 text-center text-sm text-gray-500">No results</div>
              )}
            </div>
            {!!searchText && !brandDirectory.some(b => b.toLowerCase() === searchText.toLowerCase()) && (
              <div className="flex justify-end">
                <button disabled={saving} className="px-3 py-2 rounded-lg bg-black text-white text-sm disabled:opacity-60" onClick={() => handleAddBrand(searchText.trim())}>{saving ? 'Saving…' : `Add “${searchText.trim()}”`}</button>
              </div>
            )}
          </div>
        </div>
      </div>
    )}
    </>
  );
}


